import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MarketShell from '../components/market/MarketShell';
import VariantPicker, { axisModes, shortLabel } from '../components/market/VariantPicker';
import ProductGallery from '../components/product/ProductGallery';
import { ClampText } from '../components/product/ProductPageParts';
import { marketCategoryLabel, marketLeafLabel } from '../lib/marketCategories';
import {
  defaultSku, fetchMarketProduct, fetchRelated, fetchVariantAttrs, marketImageUrl, marketShortTitle, marketTitle, optionAvailability,
  skuFor,
} from '../lib/marketCatalog';
import { cleanSpecs } from '../lib/marketSpecs';
import { extractAttributes } from '../lib/marketAttributes';
import { formatSar, pricingMode, quote, tierSavings, toSar } from '../lib/marketPricing';
import { repeatRatePct, supplierStars } from '../lib/marketRating';
import { addToCart } from '../lib/marketCart';
import { useDeliveredConfig } from '../lib/marketMode';
import { complianceNotes } from '../lib/marketCompliance';
import { airUnsuited, cardPrice, defaultMethod, needsConfirm, productCardPrice, productPrice, staffShipping, worthAdvertising, DELIVERED_T, deliveredUnit, useDeliveredInfo } from '../lib/marketDelivered';
import StarRating from '../components/market/StarRating';
import { FavoriteButton, CompareToggle, minOrderQty } from '../components/market/MarketProductCard';
import { noteViewed } from '../lib/marketLocal';
import { CardRow } from './MarketHome';
import SupplierName from '../components/market/SupplierName';
import { track } from '../lib/growthTracker';
import { observeBlocks, startProductView } from '../lib/growthEvents';
import { openInquiry } from '../lib/marketInquiries';
import { hideFromHome, isHiddenFromHome, showOnHome } from '../lib/homePicks';

// «اسأل المورد» (owner, 2026-10-30): secondary — outlined, never filled — next to «أضف للسلة».
const ASK = { ar: 'اسأل المورد', en: 'Ask the supplier', zh: '询问供应商' };

// The redesigned page's own words (owner, 2026-11-02 — web clarity redesign).
const R = {
  ar: { perPiece: 'للقطعة', tierHead: 'كل ما زادت الكمية، رخص السعر', nudge: (n, x) => `زِد ${n} قطع، وينزل سعر القطعة لـ ${x}`,
    arrives: (a, b) => `يوصلك بين ${a} و${b}`, finalPrice: 'السعر النهائي مع الشحن والجمارك يظهر في سلتك قبل الدفع. ولا ريال زيادة عند الاستلام.', safe: 'ادفع بأمان، ونتابع طلبك خطوة بخطوة',
    afterHead: 'وش يصير بعد ما تدفع؟', steps: [['نشتري من المصنع', ''], ['نجمّعها في مستودعنا بالصين', 'كل بضاعتك في كرتون واحد باسمك'], ['نشحنها ونخلّص الجمارك', 'كل التكاليف داخلة في سعرك'], ['توصلك', 'وتتابع كل خطوة من «طلباتي»']],
    priceDetails: 'تفاصيل السعر', supplierRow: 'المورد', totalFor: (n) => `الإجمالي لـ ${n} قطعة`, outStock: 'غير متوفر حالياً' },
  en: { perPiece: 'per piece', tierHead: 'The more you order, the lower the price', nudge: (n, x) => `Add ${n} more and the price drops to ${x} a piece`,
    arrives: (a, b) => `Arrives between ${a} and ${b}`, finalPrice: 'The final price with shipping and customs shows in your cart before you pay — not a riyal more on delivery.', safe: 'Pay securely; we follow your order step by step',
    afterHead: 'What happens after you pay?', steps: [['We buy from the factory', ''], ['We consolidate in our China warehouse', 'All your goods in one carton under your name'], ['We ship and clear customs', 'Every cost is in your price'], ['It reaches you', 'Follow every step in «My orders»']],
    priceDetails: 'Price details', supplierRow: 'Supplier', totalFor: (n) => `Total for ${n} pieces`, outStock: 'Out of stock' },
};
R.zh = R.en;

/** One collapsible row (closed by default). */
function Fold({ title, children, block, open = false }) {
  return (
    <details className="mk-fold" open={open} data-fold={block}>
      <summary>{title}</summary>
      <div className="mk-fold-body">{children}</div>
    </details>
  );
}

// A marketplace product page (market_products_public: listed only while the
// market is open, or to an admin).
//
// Laid out like the existing product pages (gallery left, info right; name,
// price, MOQ → options → details → seller). What is deliberately absent:
//   • any source marketplace, provider, store link or contact (brand rule);
//   • a "verified" mark — these sellers have not been verified by MAABAR,
//     and the page says so plainly;
// The supplier rating IS shown (owner approval 2026-10-19): five stars and the
// number, from the supplier's own record (lib/marketRating.js), labelled as
// that record and never as a MAABAR verification.

const T = {
  ar: { back: 'رجوع إلى المنتجات', notFound: 'المنتج غير موجود أو غير متاح.', loading: '…',
        unitPrice: 'سعر الوحدة', tiers: 'أسعار الجملة حسب الكمية', qtyFrom: 'من', per: 'لكل', credTitle: 'تقييم المورد', serviceScore: 'تقييم الخدمة العام', repeatRate: 'نسبة المشترين الذين كرروا الشراء', of5: '/ 5', save: (n) => `وفّر ${n}%`, addCart: 'أضف إلى السلة', added: 'أُضيف إلى السلة', viewCart: 'عرض السلة', fixFirst: 'صحّح الكمية أولًا', mixNote: (m) => `أقل من الحد الأدنى للمنتج (${m}) — يُحتسب الحد الأدنى على مجموع الخيارات من هذا المنتج في السلة.`, setMin: 'اضبط على الحد الأدنى', setStep: 'اضبط على أقرب مضاعف', notStep: (n) => `يُطلب بمضاعفات ${n}`, stepNote: (n) => `بمضاعفات ${n}`, setMax: 'اضبط على المتوفر', nextTier: (n, price) => `أضف ${n} للحصول على ${price} للوحدة`, range: 'الكمية', unitShort: 'سعر الوحدة', dec: 'إنقاص', inc: 'زيادة', moq: 'الحد الأدنى للطلب',
        qty: 'الكمية', belowMoq: 'أقل من الحد الأدنى للطلب', overStock: 'أكثر من المخزون المتاح',
        estTotal: 'الإجمالي', selected: 'الخيار المختار',
        stock: 'المخزون', inStock: 'متوفر', outStock: 'غير متوفر', choose: 'اختر', pickAll: 'اختر من كل الخيارات لعرض السعر والمخزون',
        specs: 'المواصفات', showAll: 'عرض كل المواصفات', showLess: 'عرض أقل', desc: 'الوصف', category: 'الفئة',
        supplier: 'المورد', fromChina: 'مورد من الصين',
        related: 'منتجات مشابهة', sameSeller: 'من المورد نفسه', zoom: 'اضغط للتكبير',
        pl: { logo: 'يطبع شعارك', custom: 'يُصنّع حسب الطلب' }, plMore: 'منتجات أخرى تقبل علامتك' },
  en: { back: 'Back to products', notFound: 'This product is not found or not available.', loading: '…',
        unitPrice: 'Unit price', tiers: 'Wholesale prices by quantity', qtyFrom: 'From', per: 'per', credTitle: 'Supplier rating', serviceScore: 'Overall service score', repeatRate: 'Buyers who ordered again', of5: '/ 5', save: (n) => `Save ${n}%`, addCart: 'Add to cart', added: 'Added to cart', viewCart: 'View cart', fixFirst: 'Fix the quantity first', mixNote: (m) => `Below the product minimum (${m}) — the minimum counts every option of this product in your cart.`, setMin: 'Set to minimum', setStep: 'Round to the next multiple', notStep: (n) => `Ordered in multiples of ${n}`, stepNote: (n) => `in multiples of ${n}`, setMax: 'Set to available', nextTier: (n, price) => `Add ${n} more to get ${price} per unit`, range: 'Quantity', unitShort: 'Unit price', dec: 'Decrease', inc: 'Increase', moq: 'Minimum order',
        qty: 'Quantity', belowMoq: 'Below the minimum order', overStock: 'More than the available stock',
        estTotal: 'Total', selected: 'Selected option',
        stock: 'Stock', inStock: 'In stock', outStock: 'Out of stock', choose: 'Choose', pickAll: 'Choose every option to see price and stock',
        specs: 'Specifications', showAll: 'Show all specifications', showLess: 'Show less', desc: 'Description', category: 'Category',
        supplier: 'Supplier', fromChina: 'Supplier in China',
        related: 'Similar products', sameSeller: 'From the same supplier', zoom: 'Click to zoom',
        pl: { logo: 'Prints your logo', custom: 'Made to order' }, plMore: 'More products that take your brand' },
  zh: { back: '返回商品', notFound: '商品不存在或不可用。', loading: '…',
        unitPrice: '单价', tiers: '按数量批发价', qtyFrom: '起', per: '每', credTitle: '供应商评分', serviceScore: '综合服务分', repeatRate: '回头率', of5: '/ 5', save: (n) => `省 ${n}%`, addCart: '加入购物车', added: '已加入购物车', viewCart: '查看购物车', fixFirst: '请先调整数量', mixNote: (m) => `低于商品起订量（${m}）— 起订量按购物车中该商品所有规格合计计算。`, setMin: '设为起订量', setStep: '调整为倍数', notStep: (n) => `须按 ${n} 的倍数订购`, stepNote: (n) => `按 ${n} 的倍数`, setMax: '设为可售数量', nextTier: (n, price) => `再加 ${n} 件可享单价 ${price}`, range: '数量', unitShort: '单价', dec: '减少', inc: '增加', moq: '起订量',
        qty: '数量', belowMoq: '低于起订量', overStock: '超过可用库存',
        estTotal: '合计', selected: '已选规格',
        stock: '库存', inStock: '有货', outStock: '缺货', choose: '选择', pickAll: '请选择所有选项以查看价格和库存',
        specs: '规格', showAll: '查看全部规格', showLess: '收起', desc: '描述', category: '类别',
        supplier: '供应商', fromChina: '中国供应商',
        related: '相似商品', sameSeller: '同一供应商', zoom: '点击放大',
        pl: { logo: '可印您的LOGO', custom: '按需生产' }, plMore: '更多可定制商品' },
};

const SPECS_COLLAPSED = 10;

export default function MarketProductDetail({ lang = 'ar', user, profile, exchangeRates }) {
  const { id } = useParams();
  const nav = useNavigate();
  const [asking, setAsking] = useState(false);   // «اسأل المورد»
  const isAr = lang === 'ar';
  const arc = isAr ? ' ar' : '';
  const c = T[lang] || T.ar;
  const font = isAr ? 'var(--font-ar)' : 'var(--font-sans)';

  const [product, setProduct] = useState(null);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState([]);
  const [qty, setQty] = useState(1);
  const [allSpecs, setAllSpecs] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [more, setMore] = useState({ related: [], sameSeller: [] });
  const [stored, setStored] = useState(null);   // market_variant_attrs, when the server has it
  // admins only: «أخفِ من الرئيسية» — off the home page pool, still in the market (owner, 2026-11-02)
  const isAdmin = ['admin', 'super_admin'].includes(String(profile?.role || '').toLowerCase());
  const [homeHidden, setHomeHidden] = useState(null);
  useEffect(() => { if (isAdmin) isHiddenFromHome(id).then(setHomeHidden); }, [isAdmin, id]);
  const toggleHome = async () => {
    const r = homeHidden ? await showOnHome(id) : await hideFromHome(id);
    if (r?.error) { console.warn('[home hidden]', r.error.message); return; }
    setHomeHidden(!homeHidden);
  };
  const r_ = R[lang] || R.ar;
  // Open at the top (owner: it sometimes opened at the footer — the scroll of the previous page).
  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  // «<short name> | مَعبر» (it said «الموردين | مَعبر» on every market page)
  useEffect(() => { if (product) document.title = `${marketShortTitle(product, lang)} | مَعبر`; }, [product, lang]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      const r = await fetchMarketProduct(id);
      if (!alive) return;
      setProduct(r.product);
      setSkus(r.skus);
      setStored(null);
      fetchVariantAttrs(id).then((x) => { if (alive) setStored(x); });
      const axes = r.product?.options?.axes || [];
      // Open on the default SKU — the one the card is priced at (market_delivered.default_sku, same rule).
      const first = defaultSku(r.skus, axes);
      setPicked(axes.map((_, a) => (first ? first.selection[a] : null)));
      setQty(minOrderQty(r.product));   // the ONE minimum rule (stated MOQ, first tier, ordering step)
      setLoading(false);
      if (r.product) {
        noteViewed(r.product.id);
        const rel = await fetchRelated(r.product.id);
        // A product appears once: same-supplier first, then similar ones not already shown.
        if (alive) setMore({ sameSeller: rel.sameSeller, related: rel.related.filter((x) => !rel.sameSeller.some((y) => y.id === x.id)) });
      }
    })();
    return () => { alive = false; };
  }, [id]);

  const axes = useMemo(() => product?.options?.axes || [], [product]);
  const avail = useMemo(() => optionAvailability(axes, skus, picked), [axes, skus, picked]);
  const modes = useMemo(() => axisModes(axes, stored), [axes, stored]);
  const sku = useMemo(() => (axes.length ? skuFor(skus, picked) : skus[0] || null), [axes, skus, picked]);
  // All pricing comes from marketPricing.quote — one rule for unit, tier and total.
  const mode = useMemo(() => pricingMode(product, skus), [product, skus]);
  const tiers = useMemo(() => (mode === 'quantity' ? tierSavings(product?.price_tiers) : []), [mode, product]);
  const q = useMemo(() => (product ? quote(product, skus, sku, qty, exchangeRates) : null), [product, skus, sku, qty, exchangeRates]);
  // The delivered-price model (owner, 2026-10-29): air and sea side by side.
  const dcfg = useDeliveredConfig();
  const dinfo = useDeliveredInfo(product?.id, !!dcfg && !!product);
  const [shipPick] = useState(null);   // the method is chosen in the cart (2026-11-02)

  // Growth OS (step 3): one view per product — dwell, the blocks it reached,
  // the last one seen, variant and quantity changes. Never affects the page.
  const view = useRef(null);
  const qNow = useRef(null);
  qNow.current = q;
  const qtySeen = useRef(null);
  const skuSeen = useRef({ ready: false });
  useEffect(() => {
    if (!product) return undefined;
    const v = startProductView(product.id, { price_sar: qNow.current?.unitSar ?? undefined, moq: minOrderQty(product) });
    view.current = v;
    qtySeen.current = null;
    skuSeen.current = { ready: false };
    return () => { v.end(); view.current = null; };
  }, [product]);
  useEffect(() => observeBlocks(view.current), [product, dinfo, picked]);
  useEffect(() => {
    if (!product) return undefined;
    if (qtySeen.current == null) { qtySeen.current = qty; return undefined; }
    if (qtySeen.current === qty) return undefined;
    const before = qtySeen.current;
    const t = setTimeout(() => {   // typing settles first: one event per change, not per keystroke
      track('product_qty_changed', { product_id: product.id, qty_before: before, qty_after: qty, below_moq: qty < minOrderQty(product), tier: qNow.current?.tier?.min_quantity });
      qtySeen.current = qty;
    }, 800);
    return () => clearTimeout(t);
  }, [qty, product]);
  useEffect(() => {
    if (!product) return;
    const key = sku?.key ?? null;
    const prev = skuSeen.current;
    if (!prev.ready) { skuSeen.current = { ready: true, key, price: qNow.current?.unitSar }; return; }
    if (prev.key === key) return;
    track('product_variant_changed', { product_id: product.id, sku_key: key, price_before: prev.price, price_after: qNow.current?.unitSar });
    skuSeen.current = { ready: true, key, price: qNow.current?.unitSar };
  }, [sku, product]);

  const gallery = useMemo(() => {
    if (!product) return [];
    const imgs = (Array.isArray(product.images) ? product.images : []).map((i) => i.ref);
    // The chosen variant's own picture leads, when it has one.
    const optionRef = axes.map((ax, a) => (picked[a] != null ? ax.values[picked[a]]?.image_ref : null)).find(Boolean);
    const lead = sku?.image_ref || optionRef;
    const refs = lead ? [lead, ...imgs.filter((r) => r !== lead)] : imgs;
    return refs.map(marketImageUrl).filter(Boolean);
  }, [product, axes, picked, sku]);

  if (loading) {
    return <MarketShell lang={lang} user={user}><div className="mk-wrap" style={{ paddingTop: 20, paddingBottom: 48 }}><p style={{ color: 'var(--text-secondary)' }}>{c.loading}</p></div></MarketShell>;
  }
  if (!product) {
    return (
      <MarketShell lang={lang} user={user}>
        <div className="mk-wrap" style={{ paddingTop: 20, paddingBottom: 48 }}>
          <button className={`fx-back${arc}`} onClick={() => nav('/market')}>{isAr ? '→ ' : '← '}{c.back}</button>
          <p style={{ color: 'var(--text-secondary)', fontFamily: font }}>{c.notFound}</p>
        </div>
      </MarketShell>
    );
  }

  const title = marketTitle(product, lang);
  const skuMoq = q.moq;
  // Before every option is picked there is no single unit price: show the range.
  const stock = sku ? Number(sku.stock ?? 0) : null;
  const allPicked = !axes.length || picked.every((p) => p != null);
  const step = q.step;
  // Stock and step are per variant and block. The minimum is the PRODUCT's,
  // met by the total of its variants (order preview 2026-10-19, see
  // marketPricing.quote) — so below it is a note here, and the cart decides.
  const belowMin = qty > 0 && qty < skuMoq;
  const qtyError = qty < 1 ? c.belowMoq : (stock != null && qty > stock ? c.overStock : (!q.stepOk ? c.notStep(step) : null));
  const total = sku && allPicked && !qtyError && q.totalSar != null ? formatSar(q.totalSar) : null;

  const unitWord = product.sale_unit ? (isAr ? product.sale_unit.ar : lang === 'zh' ? product.sale_unit.zh : product.sale_unit.en) : null;
  const setQtyClamped = (n) => setQty(Math.max(0, Math.floor(Number(n) || 0)));
  const specRows = cleanSpecs(product.specs, lang).map((r, i) => ({ i, ...r }));
  // Private label (owner, 2026-10-25): from the same registry the filters use, so the badge and the filter never disagree.
  const privateLabel = ['logo', 'custom'].filter((v) => extractAttributes(product).some((a) => a.attr === 'private_label' && a.value === v));
  const stars = supplierStars(product.seller_rating);
  const repeatPct = repeatRatePct(product.seller_rating);
  const canAdd = !!sku && allPicked && !qtyError && q.totalSar != null && stock > 0;
  const dt = DELIVERED_T[lang] || DELIVERED_T.ar;
  const dv = dcfg && dinfo ? (dinfo.skus || []).find((v) => v.key === (sku?.key ?? '')) : null;
  // «اطلب تثبيت سعر الشحن» (owner, 2026-11-01 «وحدهم»): Rule C, or a product staff price
  // (marked) — the page shows the price without shipping, no air/sea choice, one button
  const staff = !!dcfg && staffShipping(dinfo);
  const confirmIt = !!dv && q?.unitSar != null && needsConfirm(dinfo, dv, q.unitSar, dcfg);
  const cardConfirm = !dv && dcfg && dinfo ? cardPrice(product, dinfo, dcfg, (cny) => toSar(cny, exchangeRates), skuMoq) : null;
  const dd = dv && q?.unitSar != null && qty > 0 && !confirmIt ? deliveredUnit(q.unitSar, dv, dinfo.air_class, qty, dcfg) : null;
  // «أوفر» by default once the quantity reaches the sea minimum, «أسرع» below — until the buyer picks
  // Rule B (owner, 2026-10-30): air unsuited (airUnsuited) and sea offered → sea only on this
  // page — no «أسرع» card, no saving measured against air. Air stays at checkout, listed last.
  const seaOnly = !!dd && dd.sea != null && airUnsuited(dv, dinfo.air_class, q.unitSar, dcfg);
  const shipMethod = dd ? (seaOnly ? 'sea' : shipPick === 'air' || (shipPick === 'sea' && dd.sea != null) ? shipPick : defaultMethod(dv, qty, dcfg)) : null;
  // the price box before a variant is chosen: the card's hero (air at the minimum order)
  // the PRODUCT price only (owner, 2026-11-02 final display decision): the chosen variant's, else
  // the lowest across variants — shipping and customs are worked out in the cart
  // never the factory's raw price (2026-11-02): before the variants' figures arrive, the product's lowest price as a product price
  const loSar = toSar(product?.price_min_cny, exchangeRates);
  const dHero = dcfg && q?.unitSar != null && sku ? productPrice(q.unitSar, dcfg)
    : dcfg && dinfo ? productCardPrice(product, dinfo, dcfg, (cny) => toSar(cny, exchangeRates))?.price ?? null
    : dcfg && loSar != null ? productPrice(loSar, dcfg) : null;
  const showConfirm = confirmIt || (!dv && cardConfirm?.method === 'confirm');
  const dUnit = null;   // no landed price on the product page (2026-11-02): the cart shows it
  const ask = async () => {
    if (!user) { nav('/login?next=' + encodeURIComponent(`/market/${product.id}`)); return; }
    setAsking(true);
    const r = await openInquiry(product.id, sku?.key);
    setAsking(false);
    if (r.ok && r.data) nav(`/market/inquiries/${r.data}`);
  };
  const add = () => {
    if (!canAdd) return;
    addToCart({ product_id: product.id, sku_key: sku.key, qty, unit_sar: q.unitSar, unit_cny: q.unitCny });
    track('cart_item_added', { product_id: product.id, sku_key: sku.key, qty, unit_price_sar: q.unitSar, line_sar: q.totalSar, shipping_option: shipMethod });
    view.current?.added();
    setJustAdded(true);
    // «اطلب تثبيت سعر الشحن»: straight to the checkout, which sends it as one request
    if (showConfirm) nav('/market/checkout');
  };
  const specsShown = allSpecs ? specRows : specRows.slice(0, SPECS_COLLAPSED);
  const description = (isAr ? product.description_ar : product.description_en) || product.description_en || product.description_ar || '';
  const catLabel = [marketCategoryLabel(product.category, lang), marketLeafLabel(product.subcategory, lang), marketLeafLabel(product.product_type, lang)].filter(Boolean).join(' › ');

  const label = { fontSize: 11, letterSpacing: 0.6, color: 'var(--text-muted)', margin: '0 0 6px', fontFamily: font, textTransform: 'uppercase' };

  // ── the redesigned blocks (owner, 2026-11-02) ──────────────────────────────
  // A delivered per-piece price at a given unit price and quantity, by the method on show.
  const deliveredAt = (unitSar) => {
    if (unitSar == null) return null;
    // every price on the page is a product price (2026-11-02)
    return dcfg ? productPrice(unitSar, dcfg) : unitSar;
  };
  // the tier table: the supplier's REAL quantity tiers only (up to 4), each at its delivered price
  const tierRows = tiers.length > 1 ? tiers.slice(0, 4).map((t) => ({ min: t.min_quantity, unit: deliveredAt(toSar(t.price_cny, exchangeRates), Math.max(t.min_quantity, skuMoq)) })) : [];
  const nextRow = tierRows.find((t) => t.min > qty && (stock == null || t.min <= stock));
  const curRow = [...tierRows].reverse().find((t) => qty >= t.min) || null;
  const unitShown = dUnit ?? dHero;
  const totalShown = unitShown != null && qty > 0 ? Math.round(unitShown * qty * 100) / 100 : null;
  const variantSummary = axes.length && sku ? axes.map((ax, a) => shortLabel(ax.values[picked[a]], lang, true)).filter(Boolean).join(' · ') : '';
  // the folded sheet's rows: each variant's delivered price at this quantity
  const priceFor = (a, j) => {
    const s2 = skuFor(skus, picked.map((x, b) => (b === a ? j : x)));
    if (!s2) return null;
    const q2 = quote(product, skus, s2, qty, exchangeRates);
    const dv2 = dcfg && dinfo ? (dinfo.skus || []).find((v) => v.key === s2.key) : null;
    if (q2?.unitSar == null) return null;
    if (!dv2) return formatSar(q2.unitSar);
    // the same rule as the hero: a variant whose shipping staff confirm shows the price without shipping
    return formatSar(productPrice(q2.unitSar, dcfg));
  };
  const pick = (a, j) => setPicked((p) => p.map((x, b) => (b === a ? j : x)));
  const feeTableShown = dv && q?.unitSar != null && dcfg?.fee_tiered && (dcfg.fee_tiers || []).length > 1 && (() => {
    const at = (rate) => productPrice(q.unitSar, { ...dcfg, fee_rate: rate });
    return worthAdvertising(at(dcfg.fee_tiers[dcfg.fee_tiers.length - 1].rate), at(dcfg.fee_tiers[0].rate), dcfg);
  })();
  const addLabel = showConfirm ? dt.confirmAsk : c.addCart;

  return (
    <MarketShell lang={lang} user={user} stickyBar>
      <div className="mk-wrap mk-pdp" style={{ paddingTop: 20, paddingBottom: 48 }}>
        <div className="mk-crumbs" aria-label="breadcrumb" style={{ marginTop: 0 }}>
          <button type="button" onClick={() => nav('/market')}>{isAr ? 'السوق' : 'Market'}</button>
          {product.category && <><span aria-hidden="true">›</span><button type="button" onClick={() => nav(`/market/c/${product.category}`)}>{marketCategoryLabel(product.category, lang)}</button></>}
          {product.subcategory && <><span aria-hidden="true">›</span><button type="button" onClick={() => nav(`/market/c/${product.category}/${product.subcategory}`)}>{marketLeafLabel(product.subcategory, lang)}</button></>}
          {product.subcategory && product.product_type && <><span aria-hidden="true">›</span><button type="button" onClick={() => nav(`/market/c/${product.category}/${product.subcategory}/${product.product_type}`)}>{marketLeafLabel(product.product_type, lang)}</button></>}
        </div>

        <div className="mk-pdp-grid">
          <div className="mk-pdp-media">
            <ProductGallery images={gallery} alt={title} zoomTitle={c.zoom} />
          </div>

          <div className="mk-pdp-info">
            <h1 className={`fx-h1${arc}`} style={{ fontSize: isAr ? 21 : 24, marginBottom: 8, lineHeight: 1.5 }}>{title}</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, margin: '-2px 0 10px', flexWrap: 'wrap' }}>
              {product.seller_name_latin && <SupplierName className="mk-pdp-seller" name={product.seller_name_latin} prefix="✓ " />}
              {stars && <StarRating rating={product.seller_rating} size={13} compact outOf={c.of5} label={c.credTitle} />}
              <span style={{ flex: 1 }} />
              <FavoriteButton productId={product.id} user={user} lang={lang} size={32} />
              <CompareToggle productId={product.id} lang={lang} />
              {isAdmin && homeHidden !== null && (
                <button type="button" className="mk-admin-home" data-home-hidden={homeHidden ? '1' : '0'} onClick={toggleHome} title={isAr ? 'للمشرفين فقط — لا يخفي المنتج من السوق' : 'Admins only — the product stays in the market'}>
                  {homeHidden ? (isAr ? 'أعِده للرئيسية' : 'Back on the home page') : (isAr ? 'أخفِ من الرئيسية' : 'Hide from the home page')}
                </button>
              )}
            </div>
            {privateLabel.length > 0 && (
              <div data-private-label={privateLabel.join(',')} style={{ margin: '0 0 12px', display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {privateLabel.map((v) => (
                  <button key={v} type="button" className="mk-pl-chip" onClick={() => nav(`/market/search?f=private_label.${v}`)} title={c.plMore}>✓ {c.pl[v]}</button>
                ))}
              </div>
            )}

            {/* the hero: the delivered price per piece */}
            {dHero != null || unitShown != null ? (
              <div className="mk-pdp-price" data-delivered-price={unitShown ?? dHero} data-growth-block="price" data-price-confirm={showConfirm ? '1' : undefined}>
                <span className="mk-pdp-price-num"><span className="mk-ltr">{formatSar(unitShown ?? dHero)}</span> <small>{r_.perPiece}</small></span>
                {showConfirm
                  ? <span className="mk-pdp-price-label" data-confirm-line="1">{dt.confirmLine(dcfg?.confirm_hours ?? 48)}</span>
                  : <span className="mk-pdp-price-label" data-ship-in-cart="1">{dt.shipInCart}</span>}
                {showConfirm && <span className="mk-pdp-price-sub" data-confirm-sub="1">{dt.confirmSub}</span>}
              </div>
            ) : (
              <div className="mk-pdp-price" data-growth-block="price" data-price-unit={sku && q.unitSar != null ? q.unitSar : ''}>
                <span className="mk-pdp-price-num">—</span>
                {staff && <span className="mk-pdp-price-label" data-staff-price-label="1">{dt.productOnly}</span>}
              </div>
            )}

            {tierRows.length > 1 && (
              <div className="mk-tiers" data-growth-block="qty_table" data-tier-rows={tierRows.length}>
                <p className="mk-tiers-head">{r_.tierHead}</p>
                <div className="mk-tiers-row">
                  {tierRows.map((t) => {
                    const on = curRow && curRow.min === t.min;
                    return (
                      <button type="button" key={t.min} className={`mk-tier${on ? ' on' : ''}`} data-tier-min={t.min} data-tier-on={on ? '1' : '0'} onClick={() => setQty(Math.max(t.min, skuMoq))}>
                        <span className="mk-tier-qty" dir="ltr">{t.min.toLocaleString('en-US')}+</span>
                        <span className="mk-tier-price">{t.unit != null ? formatSar(t.unit) : '—'}</span>
                      </button>
                    );
                  })}
                </div>
                {nextRow && nextRow.unit != null && (unitShown == null || nextRow.unit < unitShown) && (
                  <p className="mk-tiers-nudge" data-next-tier={nextRow.min}>{r_.nudge((nextRow.min - qty).toLocaleString('en-US'), formatSar(nextRow.unit))}</p>
                )}
              </div>
            )}

            <VariantPicker axes={axes} modes={modes} picked={picked} avail={avail} onPick={pick} lang={lang} font={font} priceFor={priceFor} />

            <div className="mk-qty-box">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <label style={{ ...label, margin: 0 }} htmlFor="mq">{c.qty}{unitWord ? ` · ${unitWord}` : ''}</label>
                <div style={{ display: 'inline-flex', alignItems: 'stretch', border: '1px solid var(--border-strong)', borderRadius: 10, overflow: 'hidden' }} dir="ltr">
                  <button type="button" aria-label={c.dec} data-qty-dec="1" disabled={qty <= skuMoq}
                    onClick={() => setQtyClamped(Math.max(skuMoq, (Math.ceil(qty / step) - 1) * step))}
                    style={{ width: 38, border: 'none', background: 'var(--bg-hero)', fontSize: 18, cursor: qty <= skuMoq ? 'not-allowed' : 'pointer', opacity: qty <= skuMoq ? 0.4 : 1 }}>−</button>
                  <input id="mq" type="number" min={skuMoq} step={step} max={stock > 0 ? stock : undefined} value={qty} inputMode="numeric"
                    onChange={(e) => setQtyClamped(e.target.value)}
                    style={{ width: 84, padding: '8px 10px', border: 'none', borderInline: '1px solid var(--border)', fontSize: 15, textAlign: 'center', fontFamily: 'inherit' }} />
                  <button type="button" aria-label={c.inc} data-qty-inc="1" disabled={stock != null && qty >= stock}
                    onClick={() => setQtyClamped(stock != null ? Math.min(Math.floor(stock / step) * step, (Math.floor(qty / step) + 1) * step) : (Math.floor(qty / step) + 1) * step)}
                    style={{ width: 38, border: 'none', background: 'var(--bg-hero)', fontSize: 18, cursor: stock != null && qty >= stock ? 'not-allowed' : 'pointer', opacity: stock != null && qty >= stock ? 0.4 : 1 }}>+</button>
                </div>
                <span data-stock={stock ?? ''} style={{ fontSize: 12.5, fontFamily: font, color: stock > 0 ? 'var(--green, #2E7D32)' : 'var(--text-muted)' }}>
                  {stock > 0 ? `${c.inStock} · ${c.stock}: ${stock.toLocaleString('en-US')}` : r_.outStock}
                </span>
              </div>
              <p data-growth-block="moq" style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)', fontFamily: font }}>{c.moq}: {skuMoq.toLocaleString('en-US')}{unitWord ? ` ${unitWord}` : ''}{step > 1 ? ` · ${c.stepNote(step)}` : ''}</p>
              {qtyError && allPicked && (
                <p data-qty-error="1" style={{ margin: '8px 0 0', fontSize: 12.5, color: '#B3261E', fontFamily: font }}>
                  {qtyError} ({skuMoq.toLocaleString('en-US')}–{stock?.toLocaleString('en-US') ?? '∞'}){' '}
                  <button type="button" onClick={() => setQty(qty < 1 ? skuMoq : (stock != null && qty > stock) ? Math.floor(stock / step) * step : Math.ceil(qty / step) * step)}
                    className="mk-linkbtn">{qty < 1 ? c.setMin : (stock != null && qty > stock) ? c.setMax : c.setStep}</button>
                </p>
              )}
              {belowMin && !qtyError && allPicked && (
                <p data-below-min={skuMoq} style={{ margin: '8px 0 0', fontSize: 12.5, color: '#7A5A22', fontFamily: font }}>
                  {c.mixNote(skuMoq.toLocaleString('en-US'))}{' '}
                  <button type="button" onClick={() => setQty(skuMoq)} className="mk-linkbtn">{c.setMin}</button>
                </p>
              )}
            </div>

            {/* trust, right under the variants and quantity */}
            <ul className="mk-trust-list" data-trust-lines="1">
              <li>{r_.finalPrice}</li>
              <li>{r_.safe}</li>
            </ul>

            {/* «نثبّت لك سعر الشحن» shows once, under the price (owner, 2026-11-02: no second box) */}
            {/* the products' total at this quantity — product prices only; shipping is added in the cart */}
            {total && dHero != null && (
              <p style={{ margin: '10px 0 0', fontSize: 13, color: 'var(--text-secondary)', fontFamily: font }}>{c.estTotal}: <strong data-price-total={Math.round(dHero * qty * 100) / 100} style={{ color: 'var(--text-primary)', fontSize: 16 }}>{formatSar(Math.round(dHero * qty * 100) / 100)}</strong>
                <span style={{ color: 'var(--text-muted)', fontSize: 12 }} dir="ltr"> ({qty.toLocaleString('en-US')} × {formatSar(dHero)})</span></p>
            )}

            {/* compliance notes (owner, 2026-11-02 §2.3): the price stays; we check before buying */}
            {complianceNotes(dinfo, qty, dcfg, lang).map((n) => (
              <div key={n.key} data-compliance={n.key} className="mk-compliance-note" style={{ border: '1px solid #E8C770', background: 'rgba(232,199,112,0.10)', borderRadius: 10, padding: '10px 12px', margin: '10px 0', fontSize: 13, lineHeight: 1.7, fontFamily: font, whiteSpace: 'pre-line' }}>
                {n.title && <strong style={{ display: 'block', marginBottom: 4 }}>{n.title}</strong>}
                {n.text}
              </div>
            ))}

            {/* desktop buttons; on a phone the sticky bar below carries them */}
            <div className="mk-pdp-actions">
              <button type="button" data-add-cart="1" disabled={!canAdd} onClick={add} title={!canAdd && allPicked ? c.fixFirst : undefined} className="mk-btn mk-add-btn">{addLabel}</button>
              <button type="button" data-ask-supplier="1" onClick={ask} disabled={asking} className="mk-btn ghost mk-ask-btn">{asking ? '…' : ASK[lang] || ASK.ar}</button>
              {justAdded && (
                <span data-added="1" style={{ fontSize: 12.5, color: '#2E7D32', fontFamily: font }}>
                  ✓ {c.added} · <button type="button" onClick={() => nav('/market/cart')} className="mk-linkbtn">{c.viewCart}</button>
                </span>
              )}
            </div>

            {feeTableShown && (
              <Fold title={r_.priceDetails} block="price_details">
                <div className="mk-fee-table" data-fee-table={dcfg.fee_tiers.length}>
                  <p className="mk-ship-choose-title" style={{ margin: '0 0 4px' }}>{dt.feeTable}</p>
                  {dcfg.fee_tiers.map((t) => {
                    const unitAt = productPrice(q.unitSar, { ...dcfg, fee_rate: t.rate });
                    return (
                      <div key={t.from_sar} data-fee-tier={t.from_sar} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, padding: '3px 0', fontFamily: font }}>
                        <span>{dt.feeFrom(t.from_sar)}</span><strong>{formatSar(unitAt)}</strong>
                      </div>
                    );
                  })}
                  <p style={{ fontSize: 11.5, color: 'var(--text-muted)', margin: '4px 0 0', fontFamily: font }}>{dt.feeNote}</p>
                </div>
              </Fold>
            )}

            <section className="mk-after" data-growth-block="after_pay">
              <h2>{r_.afterHead}</h2>
              <ol>
                {r_.steps.map(([h, sub], i) => (
                  <li key={h}><span className="mk-after-n">{i + 1}</span><span><b>{h}</b>{sub && <small>{sub}</small>}</span></li>
                ))}
              </ol>
            </section>

            <div className="mk-folds">
              {specRows.length > 0 && (
                <Fold title={c.specs} block="specs">
                  {catLabel && <p style={{ margin: '0 0 8px', fontSize: 12.5, color: 'var(--text-muted)', fontFamily: font }}>{c.category}: {catLabel}</p>}
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 38%) 1fr', gap: '6px 14px', fontSize: 13, fontFamily: font }}>
                    {specsShown.map((s) => (
                      <React.Fragment key={s.i}>
                        <span style={{ color: 'var(--text-muted)' }}>{s.name}</span>
                        <span style={{ color: 'var(--text-primary)' }}>{s.value}</span>
                      </React.Fragment>
                    ))}
                  </div>
                  {specRows.length > SPECS_COLLAPSED && (
                    <button type="button" onClick={() => setAllSpecs((x) => !x)} className="mk-linkbtn" style={{ marginTop: 8 }}>
                      {allSpecs ? c.showLess : `${c.showAll} (${specRows.length})`}
                    </button>
                  )}
                </Fold>
              )}
              {description && (
                <Fold title={c.desc} block="description">
                  <ClampText lang={lang} text={description} style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.7, fontFamily: font }} />
                </Fold>
              )}
              {product.seller_name_latin && (
                <Fold title={r_.supplierRow} block="supplier">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span className="mk-seller-avatar">{product.seller_name_latin.trim().charAt(0).toUpperCase()}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14.5, fontWeight: 600, color: 'var(--text-primary)', direction: 'ltr', textAlign: isAr ? 'right' : 'left' }}><SupplierName name={product.seller_name_latin} /></div>
                      <div style={{ fontSize: 12, color: 'var(--text-secondary)', fontFamily: font }}>{c.fromChina}</div>
                      {/* The supplier's record on its original platform: five stars from the service
                          score, and the repeat-purchase rate. Not a MAABAR verification, and the page
                          does NOT say where it comes from (owner, 2026-10-22). */}
                      {(stars || repeatPct != null) && (
                        <div data-supplier-indicators="1" style={{ marginTop: 8, display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'center', fontSize: 13, fontFamily: font }}>
                          {stars && (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }} data-service-score={stars.value}>
                              <span style={{ color: 'var(--text-secondary)' }}>{c.serviceScore}</span>
                              <StarRating rating={product.seller_rating} size={16} outOf={c.of5} label={c.serviceScore} />
                            </span>
                          )}
                          {repeatPct != null && <span>{c.repeatRate}: <strong data-repeat-rate={product.seller_rating.repeat_purchase_rate} dir="ltr">{repeatPct}%</strong></span>}
                        </div>
                      )}
                    </div>
                  </div>
                </Fold>
              )}
            </div>
          </div>
        </div>
        {more.sameSeller.length > 0 && (
          <div data-same-seller={more.sameSeller.length} style={{ marginTop: 28 }}>
            <h2 style={{ fontSize: 18, fontFamily: font, margin: '0 0 8px' }}>{c.sameSeller}</h2>
            <CardRow rows={more.sameSeller} lang={lang} user={user} exchangeRates={exchangeRates} />
          </div>
        )}
        {more.related.length > 0 && (
          <div data-related={more.related.length} style={{ marginTop: 22 }}>
            <h2 style={{ fontSize: 18, fontFamily: font, margin: '0 0 8px' }}>{c.related}</h2>
            <CardRow rows={more.related} lang={lang} user={user} exchangeRates={exchangeRates} />
          </div>
        )}
      </div>

      {/* the phone's sticky bar: the chosen variant, its delivered total, ask, add */}
      <div className="mk-sticky-bar" data-sticky-bar="1">
        <div className="mk-sticky-sum">
          {variantSummary && <small data-sticky-variant="1">{variantSummary}</small>}
          <b data-sticky-total={totalShown ?? ''}>{totalShown != null ? formatSar(totalShown) : '—'}</b>
          <small>{r_.totalFor(qty.toLocaleString('en-US'))}</small>
        </div>
        <button type="button" className="mk-sticky-ask" aria-label={ASK[lang] || ASK.ar} title={ASK[lang] || ASK.ar} onClick={ask} disabled={asking} data-sticky-ask="1">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z" /><path d="M8 9h8M8 12h5" /></svg>
        </button>
        <button type="button" className="mk-btn mk-sticky-add" disabled={!canAdd} onClick={add} data-sticky-add="1">{addLabel}</button>
      </div>
    </MarketShell>
  );
}
